/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { Link } from '@/components/link';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/accordion';
import { cn } from '@/lib/utils';

type FooterLink = {
    labelKey: string;
    to: string;
    showExternalIcon?: boolean;
};

type FooterLinkSection = {
    id: string;
    headingKey: string;
    links: FooterLink[];
};

const footerLinkSections: FooterLinkSection[] = [
    {
        id: 'about-ashley',
        headingKey: 'sections.about',
        links: [
            { labelKey: 'links.about', to: '/about-us' },
            { labelKey: 'links.careers', to: 'https://careers.ashleyfurniture.com/', showExternalIcon: true },
            { labelKey: 'links.news', to: 'https://www.ashleyfurnitureindustriesllc.com/news', showExternalIcon: true },
            {
                labelKey: 'links.aboutAfi',
                to: 'https://www.ashleyfurnitureindustriesllc.com/en/company/company-overview',
                showExternalIcon: true,
            },
            {
                labelKey: 'links.becomeAStoreOwner',
                to: 'https://apply.ashleyfurnitureindustriesllc.com/agr-licensee-lead-gen-united-states/',
                showExternalIcon: true,
            },
        ],
    },
    {
        id: 'ashley-help',
        headingKey: 'sections.help',
        links: [
            { labelKey: 'links.accessibility', to: '/accessibility' },
            { labelKey: 'links.helpCenter', to: '/ask-ashley' },
            { labelKey: 'links.faq', to: '/faq' },
            { labelKey: 'links.furnitureProtectionByExtend', to: '/furniture-protection' },
            { labelKey: 'links.warrantyInformation', to: '/warranty-information' },
            { labelKey: 'links.returns', to: '/returns' },
            { labelKey: 'links.priceMatch', to: '/price-match' },
            { labelKey: 'links.childSafety', to: '/anchorit' },
            { labelKey: 'links.productsCareAndCleaning', to: '/care-and-cleaning' },
        ],
    },
    {
        id: 'services',
        headingKey: 'sections.services',
        links: [
            { labelKey: 'links.financingAndLeaseToOwnOptions', to: '/financing' },
            {
                labelKey: 'links.seeIfYouPrequalifyForFinancing',
                to: 'https://www.ashleyfurniture.com/financing/12-month-financing/PREQUAL?next=true&uniqueId=565510A04C0A508AC067D9B072C9D4F57DB34A98269D02B5&preQual=Y&siteCode=af0cflyd1',
                showExternalIcon: true,
            },
            { labelKey: 'links.orangeRewards', to: '/orange-rewards' },
            { labelKey: 'links.tradeProgram', to: '/trade' },
            { labelKey: 'links.deliveryMethods', to: '/delivery-methods' },
        ],
    },
    {
        id: 'design-resources',
        headingKey: 'sections.designResources',
        links: [
            { labelKey: 'links.designServices', to: '/interior-design-services' },
            { labelKey: 'links.blog', to: 'https://blog.ashleyfurniture.com/', showExternalIcon: true },
        ],
    },
    {
        id: 'legal',
        headingKey: 'sections.legal',
        links: [
            { labelKey: 'links.termsAndConditions', to: '/terms-and-conditions' },
            { labelKey: 'links.offersAndDetails', to: '/coupons-deals-and-offers' },
            { labelKey: 'links.termsOfUse', to: '/terms-of-use' },
            { labelKey: 'links.privacyPolicy', to: '/privacy-policy' },
            { labelKey: 'links.smsProgramTerms', to: '/sms-program-terms' },
            { labelKey: 'links.disputeResolutionProcess', to: '/drp' },
            {
                labelKey: 'links.productRecalls',
                to: 'https://www.ashleyfurnitureindustriesllc.com/consumer-notifications',
                showExternalIcon: true,
            },
            { labelKey: 'links.interestBasedAds', to: '/interest-based-ads' },
            {
                labelKey: 'links.doNotSellOrShareMyPersonalInformation',
                to: 'https://preferences.ashleyfurniture.com/',
                showExternalIcon: true,
            },
            { labelKey: 'links.privacyPreferenceCenter', to: '/privacy-preference-center' },
        ],
    },
];

const headingClassName = 'text-xs font-semibold uppercase leading-4 tracking-button text-footer-foreground';
const linkClassName =
    'inline-flex items-start gap-2 text-sm leading-5 text-footer-foreground/80 underline-offset-4 transition-colors hover:text-footer-foreground hover:underline focus-visible:text-footer-foreground focus-visible:underline';

function FooterLinkList({ links }: { links: FooterLink[] }): ReactElement {
    const { t } = useTranslation('footer');
    const translate = t as (key: string) => string;

    return (
        <ul className="flex flex-col gap-2">
            {links.map((link) => (
                <li key={link.labelKey}>
                    <Link
                        to={link.to}
                        className={linkClassName}
                        {...(link.showExternalIcon ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                        <span>{translate(link.labelKey)}</span>
                        {link.showExternalIcon ? (
                            <ExternalLink
                                aria-hidden="true"
                                className="mt-0.5 size-4 shrink-0"
                                data-testid="footer-link-external-icon"
                            />
                        ) : null}
                    </Link>
                </li>
            ))}
        </ul>
    );
}

function FooterLinkSectionContent({ section }: { section: FooterLinkSection }): ReactElement {
    const { t } = useTranslation('footer');
    const translate = t as (key: string) => string;

    return (
        <section aria-labelledby={`${section.id}-heading`} className="min-w-0">
            <h2 id={`${section.id}-heading`} className={cn(headingClassName, 'mb-4')}>
                {translate(section.headingKey)}
            </h2>
            <FooterLinkList links={section.links} />
        </section>
    );
}

interface FooterLinksProps {
    className?: string;
}

export default function FooterLinks({ className }: FooterLinksProps): ReactElement {
    const { t } = useTranslation('footer');
    const translate = t as (key: string) => string;

    return (
        <nav aria-label={translate('sections.footerLinks')} className={cn('text-footer-foreground', className)}>
            <div className="mx-auto w-full">
                <div className="hidden gap-6 lg:grid lg:grid-cols-4 lg:items-start">
                    {footerLinkSections
                        .filter((section) => section.id === 'about-ashley' || section.id === 'ashley-help')
                        .map((section) => (
                            <FooterLinkSectionContent key={section.id} section={section} />
                        ))}
                    <div className="flex min-w-0 flex-col gap-8">
                        {footerLinkSections
                            .filter((section) => section.id === 'services' || section.id === 'design-resources')
                            .map((section) => (
                                <FooterLinkSectionContent key={section.id} section={section} />
                            ))}
                    </div>
                    {footerLinkSections
                        .filter((section) => section.id === 'legal')
                        .map((section) => (
                            <FooterLinkSectionContent key={section.id} section={section} />
                        ))}
                </div>

                <Accordion type="multiple" className="w-full lg:hidden">
                    {footerLinkSections.map((section) => (
                        <AccordionItem key={section.id} value={section.id}>
                            <AccordionTrigger className="text-footer-foreground">
                                <span className="text-sm font-semibold uppercase leading-5 tracking-button">
                                    {translate(section.headingKey)}
                                </span>
                            </AccordionTrigger>
                            <AccordionContent>
                                <FooterLinkList links={section.links} />
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </nav>
    );
}
